import React, {useState} from 'react';
import {ActionIcon, Select} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Anchor} from 'src/design/atoms/Anchor';
import {Card} from 'src/design/atoms/Card';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {useMediaQuery} from '@mantine/hooks';
import {IconTrash} from '@tabler/icons-react';
import {Link} from 'react-router-dom';
import type {CourseClassroomAttendeeRosterRowResponse} from 'src/api/ProactApiClient';
import {
  CourseClassroomAttendanceEnum,
  CourseClassroomAttendeeResultEnum,
} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export interface ClassRosterTableProps {
  rows: CourseClassroomAttendeeRosterRowResponse[];
  canEdit: boolean;
  /** Used to build the certificate view link — see `certificateSessionId`. */
  definitionId: string;
  onAttendanceChange: (attendeeId: number, attendance: CourseClassroomAttendanceEnum) => void;
  onResultChange: (attendeeId: number, result: CourseClassroomAttendeeResultEnum) => void;
  onRemove: (attendeeId: number) => void;
}

const attendanceOptions = [
  {value: String(CourseClassroomAttendanceEnum.Registered), label: 'Registered'},
  {value: String(CourseClassroomAttendanceEnum.Present), label: 'Present'},
  {value: String(CourseClassroomAttendanceEnum.Absent), label: 'Absent'},
  {value: String(CourseClassroomAttendanceEnum.Excused), label: 'Excused'},
];

const resultOptions = [
  {value: String(CourseClassroomAttendeeResultEnum.Pending), label: 'Pending'},
  {value: String(CourseClassroomAttendeeResultEnum.Passed), label: 'Passed'},
  {value: String(CourseClassroomAttendeeResultEnum.Failed), label: 'Failed'},
  {value: String(CourseClassroomAttendeeResultEnum.Waived), label: 'Waived'},
];

/**
 * Roster table for the Class Workspace — one row per attendee with inline
 * attendance/result controls and a remove action. Not rendered as a form
 * field (Wave 4 convention break: a roster is not a form field).
 */
export const ClassRosterTable: React.FC<ClassRosterTableProps> = ({
  rows,
  canEdit,
  definitionId,
  onAttendanceChange,
  onResultChange,
  onRemove,
}) => {
  const [pendingRemoveId, setPendingRemoveId] = useState<number | undefined>(
    undefined
  );
  const pendingRemoveRow = rows.find(row => row.id === pendingRemoveId);
  // Six columns of inline controls only fit from lg up; below that the roster
  // becomes card-per-attendee so the Selects stay reachable without a drag.
  const isWide = useMediaQuery('(min-width: 62em)');

  const renderAttendanceSelect = (
    row: CourseClassroomAttendeeRosterRowResponse,
    width: number | string,
    label?: string
  ) => (
    <Select
      label={label}
      aria-label={`Attendance for ${row.userName}`}
      data={attendanceOptions}
      value={String(row.attendance)}
      onChange={value =>
        value &&
        onAttendanceChange(
          row.id,
          Number(value) as CourseClassroomAttendanceEnum
        )
      }
      disabled={!canEdit}
      allowDeselect={false}
      w={width}
    />
  );

  const renderResultSelect = (
    row: CourseClassroomAttendeeRosterRowResponse,
    width: number | string,
    label?: string
  ) => (
    <Select
      label={label}
      aria-label={`Result for ${row.userName}`}
      data={resultOptions}
      value={String(row.result)}
      onChange={value =>
        value &&
        onResultChange(
          row.id,
          Number(value) as CourseClassroomAttendeeResultEnum
        )
      }
      disabled={!canEdit}
      allowDeselect={false}
      w={width}
    />
  );

  const renderCertificateLink = (
    row: CourseClassroomAttendeeRosterRowResponse
  ) =>
    row.certificateSessionId != null ? (
      <Anchor
        component={Link}
        to={PathUtility.TrainingCourseSessionCertificate(
            definitionId,
            String(row.assignmentId),
            String(row.certificateSessionId)
          )}
        size="sm">
        View Certificate
      </Anchor>
    ) : (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );

  const renderRemoveAction = (row: CourseClassroomAttendeeRosterRowResponse) => (
    <Tooltip label="Remove from roster">
      <ActionIcon
        variant="subtle"
        color="red"
        aria-label={`Remove ${row.userName}`}
        onClick={() => setPendingRemoveId(row.id)}>
        <IconTrash size={16} />
      </ActionIcon>
    </Tooltip>
  );

  const removeDialog = (
    <ConfirmDialog
      opened={pendingRemoveId !== undefined}
      onClose={() => setPendingRemoveId(undefined)}
      onConfirm={() => {
        if (pendingRemoveId !== undefined) {
          onRemove(pendingRemoveId);
        }
        setPendingRemoveId(undefined);
      }}
      confirmationButtonColor="red"
      title="Remove from roster?"
      body={`Remove ${pendingRemoveRow?.userName ?? 'this person'} from the class roster? This does not change their course assignment.`}
    />
  );

  if (rows.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No one is on the roster yet. Use "Add Attendees" to build the class
        from people already assigned to this course.
      </Text>
    );
  }

  if (!isWide) {
    return (
      <>
        <Stack gap="sm">
          {rows.map(row => (
            <Card key={row.id} withBorder shadow="xs" p="md">
              <Stack gap="xs">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <div style={{minWidth: 0}}>
                    <Text fw={500} size="sm">
                      {row.userName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {[row.employeeId, row.departmentName, row.vendorName]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </Text>
                    {!row.assignmentActive && (
                      <Text size="xs" c="dimmed">
                        Assignment inactive
                      </Text>
                    )}
                  </div>
                  {canEdit && renderRemoveAction(row)}
                </Group>
                {renderAttendanceSelect(row, '100%', 'Attendance')}
                {renderResultSelect(row, '100%', 'Result')}
                {renderCertificateLink(row)}
              </Stack>
            </Card>
          ))}
        </Stack>
        {removeDialog}
      </>
    );
  }

  return (
    <>
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Person</Table.Th>
              <Table.Th>Employee ID</Table.Th>
              <Table.Th>Department</Table.Th>
              <Table.Th>Vendor</Table.Th>
              <Table.Th>Attendance</Table.Th>
              <Table.Th>Result</Table.Th>
              <Table.Th>Certificate</Table.Th>
              {canEdit && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(row => (
              <Table.Tr key={row.id}>
                <Table.Td>
                  <Text fw={500} size="sm">
                    {row.userName}
                  </Text>
                  {!row.assignmentActive && (
                    <Text size="xs" c="dimmed">
                      Assignment inactive
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{row.employeeId ?? '—'}</Table.Td>
                <Table.Td>{row.departmentName ?? '—'}</Table.Td>
                <Table.Td>{row.vendorName ?? '—'}</Table.Td>
                <Table.Td>{renderAttendanceSelect(row, 140)}</Table.Td>
                <Table.Td>{renderResultSelect(row, 130)}</Table.Td>
                <Table.Td>{renderCertificateLink(row)}</Table.Td>
                {canEdit && (
                  <Table.Td>
                    <Group justify="flex-end">{renderRemoveAction(row)}</Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {removeDialog}
    </>
  );
};
